import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import { SettingsProvider } from "@/lib/settings";
import { CartProvider } from "@/lib/cart";
import { ContentProvider } from "@/lib/content";
import { CustomerProvider } from "@/lib/customer";
import CustomerAuthCallback from "@/components/CustomerAuthCallback";
import Connexion from "@/pages/compte/Connexion";
import MonCompte from "@/pages/compte/MonCompte";
import ReinitialiserClient from "@/pages/compte/ReinitialiserClient";
import TextesAdmin from "@/pages/admin/TextesAdmin";
import CategoriesAdmin from "@/pages/admin/CategoriesAdmin";
import CompteAdmin from "@/pages/admin/CompteAdmin";
import PromosAdmin from "@/pages/admin/PromosAdmin";
import ResetPassword from "@/pages/admin/ResetPassword";
import { initLenis, destroyLenis, scrollToEl, scrollToTop } from "@/lib/smooth";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PromoBanner from "@/components/PromoBanner";
import TestimonialsAdmin from "@/pages/admin/TestimonialsAdmin";
import Home from "@/pages/Home";
import Boutique from "@/pages/Boutique";
import Produit from "@/pages/Produit";
import Blog from "@/pages/Blog";
import Article from "@/pages/Article";
import Commande from "@/pages/Commande";
import Merci from "@/pages/Merci";
import Services from "@/pages/Services";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Products from "@/pages/admin/Products";
import Orders from "@/pages/admin/Orders";
import BlogAdmin from "@/pages/admin/BlogAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center text-center px-6">
          <div>
            <p className="font-serif text-3xl text-teal-dark">Oups, une petite erreur est survenue.</p>
            <button onClick={() => window.location.reload()} className="mt-6 rounded-full bg-teal px-8 py-3 text-cream">
              Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function LegacyArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/conseils/${slug}`} replace />;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    if (hash) {
      const t = setTimeout(() => scrollToEl(hash), 400);
      return () => clearTimeout(t);
    }
    scrollToTop();
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const location = useLocation();
  const { pathname } = location;
  const isAdmin = pathname.startsWith("/admin");
  useEffect(() => {
    if (isAdmin) destroyLenis();
    else initLenis();
  }, [isAdmin]);
  if (location.hash?.includes("session_id=")) return <CustomerAuthCallback />;
  return (
    <>
      {!isAdmin && <PromoBanner />}
      {!isAdmin && <Nav />}
      <CartDrawer />
      <ScrollManager />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/boutique" element={<Boutique />} />
        <Route path="/produit/:slug" element={<Produit />} />
        <Route path="/conseils" element={<Blog />} />
        <Route path="/conseils/:slug" element={<Article />} />
        <Route path="/journal" element={<Navigate to="/conseils" replace />} />
        <Route path="/journal/:slug" element={<LegacyArticleRedirect />} />
        <Route path="/commander" element={<Commande />} />
        <Route path="/merci" element={<Merci />} />
        <Route path="/accompagnement" element={<Services />} />
        <Route path="/compte" element={<MonCompte />} />
        <Route path="/compte/connexion" element={<Connexion />} />
        <Route path="/compte/reinitialiser" element={<ReinitialiserClient />} />
        <Route path="/admin/reinitialiser" element={<ResetPassword />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="produits" element={<Products />} />
          <Route path="commandes" element={<Orders />} />
          <Route path="journal" element={<BlogAdmin />} />
          <Route path="avis" element={<TestimonialsAdmin />} />
          <Route path="textes" element={<TextesAdmin />} />
          <Route path="categories" element={<CategoriesAdmin />} />
          <Route path="compte" element={<CompteAdmin />} />
          <Route path="promos" element={<PromosAdmin />} />
          <Route path="reglages" element={<SettingsAdmin />} />
        </Route>
        <Route path="*" element={<Home />} />
      </Routes>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppButton />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <SettingsProvider>
          <ContentProvider>
          <CustomerProvider>
          <CartProvider>
            <BrowserRouter>
              <Shell />
            </BrowserRouter>
            <Toaster position="top-center" richColors />
          </CartProvider>
          </CustomerProvider>
          </ContentProvider>
        </SettingsProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
